import React, { useEffect, useState } from "react";
import {
  Bell,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronRight as ArrowRight,
  Edit2,
  FileText,
  Info,
  Pill,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Empty,
  Input,
  message,
  Modal,
  Spin,
  Tag,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import MedicineReminderService from "../../Services/MedicineReminderService";
import DrugServices from "../../Services/DrugServices";
import AuthService from "../../Services/AuthService";
import Navbar from "../../DashBoardComponents/Navbar";

const { TextArea } = Input;

const daysOfWeek = [
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
  { key: "sunday", label: "Sun" },
];

const emptySchedule = {
  notificationFromDate: "",
  notificationToDate: "",
  monday: "N",
  tuesday: "N",
  wednesday: "N",
  thursday: "N",
  friday: "N",
  saturday: "N",
  sunday: "N",
};

const intakeOptions = [
  { label: "After Food", value: "AF" },
  { label: "Before Food", value: "BF" },
];

const getIntakeTypeLabel = (value) => {
  if (value === "BF") return "Before Food";
  if (value === "AF") return "After Food";
  if (value === "Before Food") return "Before Food";
  if (value === "After Food") return "After Food";
  return "After Food";
};

const normalizeIntakeType = (value) => {
  if (value === "BF" || value === "Before Food") return "BF";
  return "AF";
};

const createEmptyForm = () => ({
  drugName: "",
  morningAppl: "N",
  morningTime: "08:00",
  afternoonAppl: "N",
  afternoonTime: "13:00",
  eveningAppl: "N",
  eveningTime: "18:00",
  notificationFromDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
  notificationToDate: dayjs().add(7, "day").format("YYYY-MM-DD HH:mm:ss"),
  intakeType: "AF",
  instruction: "",
  monday: "N",
  tuesday: "N",
  wednesday: "N",
  thursday: "N",
  friday: "N",
  saturday: "N",
  sunday: "N",
});

const MedicineReminder = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [baseDate, setBaseDate] = useState(dayjs());

  const [medicines, setMedicines] = useState([]);
  const [searchingMedicines, setSearchingMedicines] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [isPrescModalOpen, setIsPrescModalOpen] = useState(false);
  const [prescriptions, setPrescriptions] = useState([]);
  const [loadingPresc, setLoadingPresc] = useState(false);
  const [isPrescriptionMode, setIsPrescriptionMode] = useState(false);

  const [commonSchedule, setCommonSchedule] = useState(emptySchedule);
  const [form, setForm] = useState(createEmptyForm());
  const [medicationList, setMedicationList] = useState([]);
  const [currentEditIndex, setCurrentEditIndex] = useState(null);
  const [prescriptionId, setPrescriptionId] = useState("");
  const [dosagePattern, setDosagePattern] = useState("0-0-0");

  const { loggedUser } = useSelector((state) => state.auth);
  const patientId =
    loggedUser?.patientId ||
    JSON.parse(localStorage.getItem("patientData"))?.patientId;

  useEffect(() => {
    fetchReminders();
  }, [selectedDate, patientId]);

  const fetchReminders = async () => {
    if (!patientId) return;

    setLoading(true);
    try {
      const payload = {
        params: {
          patientId,
          fromDate: selectedDate.format("MM/DD/YYYY"),
          toDate: selectedDate.format("MM/DD/YYYY"),
          status: 1,
        },
      };
      const response = await MedicineReminderService.listReminders(payload);
      setReminders(response.data || []);
    } catch (error) {
      console.error("Error fetching reminders:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchMedicines = async (value) => {
    setForm({ ...form, drugName: value });

    const searchText = value.trim();

    if (searchText.length < 2) {
      setMedicines([]);
      setShowSuggestions(false);
      return;
    }

    setSearchingMedicines(true);
    try {
      const response = await DrugServices.fetchDrugList(searchText, 1, 20);
      const results = response.data.data || [];

      setMedicines(results);
      setShowSuggestions(results.length > 0);
    } catch (error) {
      console.error("Error searching medicines:", error);
      setMedicines([]);
      setShowSuggestions(false);
    } finally {
      setSearchingMedicines(false);
    }
  };

  const selectMedicine = (medName) => {
    setForm({ ...form, drugName: medName });
    setShowSuggestions(false);
    setMedicines([]);
  };

  const fetchPrescriptions = async () => {
    if (!patientId) {
      message.warning("Patient identification missing. Please login again.");
      return;
    }

    setLoadingPresc(true);
    setIsPrescModalOpen(true);

    try {
      const payload = {
        patientId,
        detailsYN: "Y",
        status: 2,
        pageNo: 1,
        pageSize: 50,
      };
      const response = await AuthService.getMyPrescriptionView(payload);
      setPrescriptions(response.data || []);
    } catch (error) {
      console.error("Error fetching prescriptions:", error);
      message.error("Failed to load prescriptions");
    } finally {
      setLoadingPresc(false);
    }
  };

  const handleSelectPrescription = (selectedPrescription) => {
    setPrescriptionId(selectedPrescription.consultationId);

    const meds =
      selectedPrescription.drugPrescription?.map((med) => {
        const dosageParts = med.dosage
          ? med.dosage.split("-").map((item) => item.trim())
          : ["0", "0", "0"];

        return {
          drugName: med.drugName || med.medicineName,
          instruction: med.instruction || med.notes || "",
          intakeType: normalizeIntakeType(med.afterBeforeFood),
          morningAppl: dosageParts[0] === "1" ? "Y" : "N",
          afternoonAppl: dosageParts[1] === "1" ? "Y" : "N",
          eveningAppl: dosageParts[2] === "1" ? "Y" : "N",
          morningTime: "08:00",
          afternoonTime: "13:00",
          eveningTime: "18:00",
          notificationFromDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          notificationToDate: dayjs().add(7, "day").format("YYYY-MM-DD HH:mm:ss"),
          monday: "N",
          tuesday: "N",
          wednesday: "N",
          thursday: "N",
          friday: "N",
          saturday: "N",
          sunday: "N",
        };
      }) || [];

    setMedicationList(meds);
    setCommonSchedule(emptySchedule);
    setIsPrescriptionMode(true);
    setIsPrescModalOpen(false);
    setIsModalOpen(true);
  };

  const handleDosageChange = (pattern) => {
    setDosagePattern(pattern);
    const [morning, afternoon, evening] = pattern.split("-");

    setForm({
      ...form,
      morningAppl: morning === "1" ? "Y" : "N",
      afternoonAppl: afternoon === "1" ? "Y" : "N",
      eveningAppl: evening === "1" ? "Y" : "N",
    });
  };

  const addMedicineToList = () => {
    if (!form.drugName.trim()) {
      message.error("Please enter a medicine name");
      return;
    }

    if (currentEditIndex !== null) {
      const newList = [...medicationList];
      newList[currentEditIndex] = { ...form };
      setMedicationList(newList);
      setCurrentEditIndex(null);
      message.success("Medicine updated in list");
    } else {
      setMedicationList([...medicationList, { ...form }]);
      message.success("Medicine added to list");
    }

    setForm({
      ...form,
      drugName: "",
      morningAppl: "N",
      afternoonAppl: "N",
      eveningAppl: "N",
      instruction: "",
    });
    setDosagePattern("0-0-0");
    setMedicines([]);
    setShowSuggestions(false);
  };

  const handleSave = async () => {
    if (!editingReminder && medicationList.length === 0) {
      message.error("Please add at least one medicine to the list");
      return;
    }

    if (editingReminder && !form.drugName.trim()) {
      message.error("Please enter a medicine name");
      return;
    }

    if (!commonSchedule.notificationFromDate || !commonSchedule.notificationToDate) {
      message.error("Please select both From Date and To Date");
      return;
    }

    const hasDays = daysOfWeek.some((day) => commonSchedule[day.key] === "Y");
    if (!hasDays) {
      message.error("Please select at least one Repeat Day");
      return;
    }

    setLoading(true);

    try {
      if (editingReminder) {
        const payload = {
          ...form,
          ...commonSchedule,
          patientId,
          prescriptionId: prescriptionId || null,
          modifiedBy: patientId,
          modifiedDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          status: 1,
          morningTime:
            form.morningAppl === "Y"
              ? form.morningTime?.substring(0, 5) || "08:00"
              : null,
          afternoonTime:
            form.afternoonAppl === "Y"
              ? form.afternoonTime?.substring(0, 5) || "13:00"
              : null,
          eveningTime:
            form.eveningAppl === "Y"
              ? form.eveningTime?.substring(0, 5) || "18:00"
              : null,
        };

        await MedicineReminderService.updateReminder([payload]);
        message.success("Reminder updated successfully");
      } else {
        const batchPayload = medicationList.map((med) => ({
          ...med,
          ...commonSchedule,
          patientId,
          prescriptionId: prescriptionId || null,
          createdBy: patientId,
          modifiedBy: patientId,
          accountId: loggedUser?.account_Id,
          clinicId: loggedUser?.clinic_Id,
          createdDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          modifiedDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          currentDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          status: 1,
          morningTime:
            med.morningAppl === "Y"
              ? med.morningTime?.substring(0, 5) || "08:00"
              : null,
          afternoonTime:
            med.afternoonAppl === "Y"
              ? med.afternoonTime?.substring(0, 5) || "13:00"
              : null,
          eveningTime:
            med.eveningAppl === "Y"
              ? med.eveningTime?.substring(0, 5) || "18:00"
              : null,
        }));

        await MedicineReminderService.saveReminder(batchPayload);
        message.success(`${medicationList.length} reminders saved successfully`);
      }

      setIsModalOpen(false);
      fetchReminders();
      resetForm();
    } catch (error) {
      console.error("Error saving reminders:", error);
      message.error(editingReminder ? "Failed to update reminder" : "Failed to save reminders");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(createEmptyForm());
    setCommonSchedule(emptySchedule);
    setEditingReminder(null);
    setMedicationList([]);
    setCurrentEditIndex(null);
    setDosagePattern("0-0-0");
    setPrescriptionId("");
    setIsPrescriptionMode(false);
    setShowSuggestions(false);
    setMedicines([]);
  };

  const editReminder = (reminder) => {
    setEditingReminder(reminder);
    setIsPrescriptionMode(false);

    setCommonSchedule({
      notificationFromDate: reminder.notificationFromDate
        ? dayjs(reminder.notificationFromDate).format("YYYY-MM-DD HH:mm:ss")
        : "",
      notificationToDate: reminder.notificationToDate
        ? dayjs(reminder.notificationToDate).format("YYYY-MM-DD HH:mm:ss")
        : "",
      monday: reminder.monday || "N",
      tuesday: reminder.tuesday || "N",
      wednesday: reminder.wednesday || "N",
      thursday: reminder.thursday || "N",
      friday: reminder.friday || "N",
      saturday: reminder.saturday || "N",
      sunday: reminder.sunday || "N",
    });

    setForm({
      ...reminder,
      intakeType: normalizeIntakeType(reminder.intakeType),
      morningTime: reminder.morningTime?.substring(0, 5) || "08:00",
      afternoonTime: reminder.afternoonTime?.substring(0, 5) || "13:00",
      eveningTime: reminder.eveningTime?.substring(0, 5) || "18:00",
    });
    setDosagePattern(
      `${reminder.morningAppl === "Y" ? "1" : "0"}-${reminder.afternoonAppl === "Y" ? "1" : "0"
      }-${reminder.eveningAppl === "Y" ? "1" : "0"}`
    );

    setMedicationList([]);
    setIsModalOpen(true);
  };

  const closeScheduleModal = () => {
    setIsModalOpen(false);
    setMedicationList([]);
    setIsPrescriptionMode(false);
    setShowSuggestions(false);
    setMedicines([]);
  };

  const renderScheduleConfig = () => (
    <div className="bg-white p-5 rounded-2xl border border-gray-100 mb-6 shadow-sm">
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs font-bold text-gray-400 uppercase mb-2 block">
            From Date
          </label>
          <DatePicker
            value={
              commonSchedule.notificationFromDate
                ? dayjs(commonSchedule.notificationFromDate)
                : null
            }
            onChange={(date) =>
              setCommonSchedule({
                ...commonSchedule,
                notificationFromDate: date ? date.format("YYYY-MM-DD HH:mm:ss") : "",
              })
            }
            className="w-full h-12 rounded-xl border-gray-100"
            format="DD/MM/YYYY"
          />
        </div>

        <div>
          <label className="text-xs font-bold text-gray-400 uppercase mb-2 block">
            To Date
          </label>
          <DatePicker
            value={
              commonSchedule.notificationToDate
                ? dayjs(commonSchedule.notificationToDate)
                : null
            }
            onChange={(date) =>
              setCommonSchedule({
                ...commonSchedule,
                notificationToDate: date ? date.format("YYYY-MM-DD HH:mm:ss") : "",
              })
            }
            className="w-full h-12 rounded-xl border-gray-100"
            format="DD/MM/YYYY"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-bold text-gray-400 uppercase mb-2 block">
          Repeat Days
        </label>
        <div className="flex items-center gap-4 flex-wrap bg-gray-50 p-4 rounded-xl border border-gray-100">
          <Checkbox
            checked={daysOfWeek.every((day) => commonSchedule[day.key] === "Y")}
            onChange={(e) => {
              const value = e.target.checked ? "Y" : "N";
              setCommonSchedule({
                ...commonSchedule,
                monday: value,
                tuesday: value,
                wednesday: value,
                thursday: value,
                friday: value,
                saturday: value,
                sunday: value,
              });
            }}
            className="font-bold text-[#071a3d]"
          >
            All
          </Checkbox>

          {daysOfWeek.map((day) => (
            <Checkbox
              key={day.key}
              checked={commonSchedule[day.key] === "Y"}
              onChange={(e) =>
                setCommonSchedule({
                  ...commonSchedule,
                  [day.key]: e.target.checked ? "Y" : "N",
                })
              }
              className="font-semibold text-gray-600"
            >
              {day.label}
            </Checkbox>
          ))}
        </div>
      </div>
    </div>
  );

  const renderDateStrip = () => {
    const dates = Array.from({ length: 7 }, (_, index) => baseDate.add(index, "day"));

    return (
      <div className="flex items-center justify-between gap-2 overflow-x-auto pb-4 hide-scrollbar">
        {dates.map((date, index) => {
          const isSelected = date.isSame(selectedDate, "day");

          return (
            <div
              key={index}
              onClick={() => setSelectedDate(date)}
              className={`flex flex-col items-center justify-center min-w-[70px] h-[90px] rounded-2xl cursor-pointer transition-all duration-300 ${isSelected
                ? "bg-[#189FAA] text-white shadow-lg scale-105"
                : "bg-white text-gray-500 border border-gray-100 hover:border-[#189FAA]"
                }`}
            >
              <span className="text-xs font-medium uppercase">{date.format("ddd")}</span>
              <span className="text-2xl font-bold mt-1">{date.format("DD")}</span>
              <span className="text-[10px] uppercase">{date.format("MMM")}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const getMedicineDaysText = (med) => {
    const allDays = daysOfWeek.every((day) => med[day.key] === "Y");
    if (allDays) return "Daily";

    return (
      daysOfWeek
        .filter((day) => med[day.key] === "Y")
        .map((day) => day.label)
        .join(", ") || ""
    );
  };

  const renderReminderCard = (item, index) => (
    <Card
      key={`${item.id || item.drugName || index}`}
      className="rounded-[24px] border-none shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-4">
          <div className="w-12 h-12 bg-[#189FAA]/10 rounded-2xl flex items-center justify-center">
            <Pill color="#189FAA" size={24} />
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-lg font-bold text-[#071a3d]">{item.drugName}</h4>

            </div>

            <Tag color="green" className="bg-green-50 text-green-600 border-none rounded-lg px-3 py-1 font-semibold text-xs mb-3">
              {getIntakeTypeLabel(item.intakeType)}
            </Tag>

            {item?.instruction && (
              <div className="flex items-start gap-2 mt-2">
                <Info size={16} className="text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase">Instruction</p>
                  <p className="text-sm text-gray-400 whitespace-pre-line">
                    {item.instruction}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-3 shrink-0">
          {/* <Button
            icon={<Edit2 size={16} />}
            onClick={() => editReminder(item)}
            className="flex items-center gap-1 font-semibold border-none text-[#189FAA] bg-transparent"
          >
            Edit
          </Button> */}

          <div className="flex flex-col items-center bg-gray-50 px-4 py-2 rounded-2xl border border-gray-100 min-w-[100px]">
            {/* <span className="text-[10px] font-bold text-gray-400 uppercase">1 Tablet</span> */}
            <span className="text-sm font-bold text-gray-700">Frequency</span>
            <div className="mt-1 font-bold text-[#189FAA]">
              {item.morningAppl === "Y" ? "1" : "0"}-
              {item.afternoonAppl === "Y" ? "1" : "0"}-
              {item.eveningAppl === "Y" ? "1" : "0"}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );

  const renderMedicineListPreview = () => (
    <div className="bg-gray-50 rounded-2xl p-4 flex flex-col border border-gray-100 flex-1 min-h-[300px]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-[#071a3d]">
          Added Medicines ({medicationList.length})
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
        {medicationList.length > 0 ? (
          <div className="space-y-3">
            {medicationList.map((med, index) => (
              <div
                key={`${med.drugName}-${index}`}
                className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 group relative"
              >
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <div className="font-bold text-sm text-[#071a3d] leading-none">
                      {med.drugName}
                    </div>
                    <div className="text-[10px] text-gray-400 font-bold uppercase mt-1.5 bg-gray-50 inline-block px-1.5 py-0.5 rounded">
                      {med.morningAppl === "Y" ? "1" : "0"}-
                      {med.afternoonAppl === "Y" ? "1" : "0"}-
                      {med.eveningAppl === "Y" ? "1" : "0"} • {getIntakeTypeLabel(med.intakeType)} •{" "}
                      {getMedicineDaysText(med)}
                    </div>
                  </div>

                  <div className="flex opacity-0 group-hover:opacity-100 transition-opacity">
                    {!isPrescriptionMode && (
                      <Button
                        type="text"
                        size="small"
                        icon={<Edit2 size={14} className="text-[#189FAA]" />}
                        onClick={() => {
                          setForm(med);
                          setCurrentEditIndex(index);
                          setDosagePattern(
                            `${med.morningAppl === "Y" ? "1" : "0"}-${med.afternoonAppl === "Y" ? "1" : "0"
                            }-${med.eveningAppl === "Y" ? "1" : "0"}`
                          );
                        }}
                      />
                    )}

                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<Trash2 size={14} />}
                      onClick={() =>
                        setMedicationList(medicationList.filter((_, itemIndex) => itemIndex !== index))
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-300">
            <Pill size={40} color="#e5e7eb" />
            <p className="text-sm mt-2">No medicines added yet</p>
          </div>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <Button
          type="primary"
          block
          size="large"
          onClick={handleSave}
          loading={loading}
          disabled={medicationList.length === 0}
          className="h-14 rounded-2xl bg-[#189FAA] border-none font-bold shadow-lg text-white"
        >
          Save All Reminders {medicationList.length > 0 && `(${medicationList.length})`}
        </Button>
      </div>
    </div>
  );

  const currentDayName = selectedDate.format("dddd").toLowerCase();
  const filteredReminders = reminders.filter((item) => item[currentDayName] === "Y");
  const morningMeds = filteredReminders.filter((item) => item.morningAppl === "Y");
  const afternoonMeds = filteredReminders.filter((item) => item.afternoonAppl === "Y");
  const eveningMeds = filteredReminders.filter((item) => item.eveningAppl === "Y");

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-20 pt-[90px]">
      <Navbar />

      <div className="max-w-2xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white rounded-2xl shadow-sm">
              <Bell className="text-[#189FAA]" size={24} />
            </div>
            <h1 className="text-2xl font-bold text-[#071a3d]">Medicine Reminder</h1>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={fetchPrescriptions}
              className="h-12 px-6 rounded-xl border-[#189FAA] text-[#189FAA] font-semibold hover:bg-[#189FAA]/5 flex items-center gap-2"
            >
              <FileText size={18} /> Select Prescription
            </Button>

            <Button
              type="primary"
              icon={<Plus size={18} />}
              onClick={() => {
                resetForm();
                setIsModalOpen(true);
              }}
              className="h-12 px-6 rounded-xl bg-[#189FAA] border-none shadow-md hover:scale-105 transition-transform font-semibold text-white"
            >
              Add Schedule
            </Button>
          </div>
        </div>

        <div className="bg-white p-6 rounded-[30px] shadow-sm mb-6 border border-gray-50">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-gray-800">Select Date</h3>
            <div className="flex gap-2">
              <button
                onClick={() => setBaseDate(baseDate.subtract(1, "day"))}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft size={20} className="text-gray-400" />
              </button>
              <button
                onClick={() => setBaseDate(baseDate.add(1, "day"))}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight size={20} className="text-gray-400" />
              </button>
            </div>
          </div>
          {renderDateStrip()}
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-800 text-lg">Medicines</h3>
          </div>

          {loading ? (
            <div className="flex justify-center p-20">
              <Spin size="large" />
            </div>
          ) : filteredReminders.length === 0 ? (
            <div className="bg-white p-12 rounded-[30px] border border-dashed border-gray-200 text-center">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bell className="text-gray-300" size={32} />
              </div>
              <h4 className="text-gray-400 font-medium">
                No medicines scheduled for today
              </h4>
              <p className="text-gray-300 text-sm mt-1">
                Select a prescription or add a medicine to build the schedule.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {morningMeds.length > 0 && (
                <div className="bg-blue-50/30 border border-blue-100 rounded-2xl p-4">
                  <h4 className="font-bold text-[#071a3d] mb-3 flex items-center gap-2">
                    Morning
                    <Tag color="blue" className="rounded-full border-none">
                      {morningMeds.length}
                    </Tag>
                  </h4>
                  <div className="flex flex-col gap-4 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
                    {morningMeds.map(renderReminderCard)}
                  </div>
                </div>
              )}

              {afternoonMeds.length > 0 && (
                <div className="bg-orange-50/30 border border-orange-100 rounded-2xl p-4">
                  <h4 className="font-bold text-[#071a3d] mb-3 flex items-center gap-2">
                    Afternoon
                    <Tag color="orange" className="rounded-full border-none">
                      {afternoonMeds.length}
                    </Tag>
                  </h4>
                  <div className="flex flex-col gap-4 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
                    {afternoonMeds.map(renderReminderCard)}
                  </div>
                </div>
              )}

              {eveningMeds.length > 0 && (
                <div className="bg-purple-50/30 border border-purple-100 rounded-2xl p-4">
                  <h4 className="font-bold text-[#071a3d] mb-3 flex items-center gap-2">
                    Evening
                    <Tag color="purple" className="rounded-full border-none">
                      {eveningMeds.length}
                    </Tag>
                  </h4>
                  <div className="flex flex-col gap-4 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
                    {eveningMeds.map(renderReminderCard)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Modal
        title={null}
        open={isModalOpen}
        onCancel={closeScheduleModal}
        footer={null}
        width={950}
        centered
        className="medicine-modal"
        styles={{
          mask: { backdropFilter: "blur(4px)" },
          content: { borderRadius: "28px", padding: 0, overflow: "visible" },
        }}
      >
        <div className="p-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-[#071a3d]">
              {editingReminder
                ? "Edit Medicine"
                : isPrescriptionMode
                  ? "Schedule Prescription Medicines"
                  : "Add Medicine Schedule"}
            </h2>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {(!isPrescriptionMode || editingReminder) && (
              <div className="flex-1 space-y-6 max-h-[600px] overflow-y-auto pr-4 custom-scrollbar">
                <div className="relative">
                  <label className="text-xs font-bold text-gray-400 uppercase mb-2 block">
                    Medicine Name
                  </label>

                  <div className="relative">
                    <Input
                      placeholder="Type medicine name"
                      className="w-full h-12 rounded-xl border-gray-100 pr-10 font-semibold"
                      value={form.drugName}
                      onChange={(e) => handleSearchMedicines(e.target.value)}
                      onFocus={() => {
                        if (form.drugName.trim().length >= 2 && medicines.length > 0) {
                          setShowSuggestions(true);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowSuggestions(false), 150);
                      }}
                    />

                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {searchingMedicines ? (
                        <Spin size="small" />
                      ) : (
                        <Search size={18} className="text-gray-300" />
                      )}
                    </div>
                  </div>

                  {showSuggestions && medicines.length > 0 && (
                    <div className="absolute z-[1001] left-0 right-0 mt-1 bg-white border border-gray-100 rounded-2xl shadow-xl max-h-[200px] overflow-y-auto overflow-x-hidden p-2">
                      <ul className="m-0 p-0 list-none">
                        {medicines.map((med, index) => (
                          <li
                            key={index}
                            onMouseDown={() =>
                              selectMedicine(med.medicineName || med.drugName)
                            }
                            className="px-4 py-3 hover:bg-gray-50 rounded-xl cursor-pointer transition-colors flex items-center gap-3 border-b border-gray-50 last:border-none"
                          >
                            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                              <Pill size={16} color="#189FAA" />
                            </div>
                            <div>
                              <div className="font-bold text-[#071a3d] leading-tight">
                                {med.medicineName || med.drugName}
                              </div>
                              <div className="text-[10px] text-gray-400 uppercase font-medium">
                                {med.drugCategory || "General"}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {editingReminder && renderScheduleConfig()}

                <div>
                  <div className="flex items-center gap-1 mb-2">
                    <label className="text-xs font-bold text-gray-400 uppercase block">
                      Dosage (Frequency)
                    </label>
                    <Tooltip title="Morning - Afternoon - Evening">
                      <Info size={14} className="text-gray-300" />
                    </Tooltip>
                  </div>

                  <div className="grid grid-cols-5 gap-2">
                    {["1-1-1", "1-0-1", "1-0-0", "0-1-0", "0-0-1"].map(
                      (pattern) => (
                        <button
                          key={pattern}
                          onClick={() => handleDosageChange(pattern)}
                          className={`h-10 rounded-xl border text-[11px] font-bold transition-all ${dosagePattern === pattern
                            ? "bg-[#189FAA]/10 border-[#189FAA] text-[#189FAA]"
                            : "bg-white border-gray-100 text-gray-600 hover:border-gray-300"
                            }`}
                        >
                          {pattern}
                        </button>
                      )
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase mb-2 block">
                    When to Take
                  </label>

                  <div className="flex gap-4">
                    {intakeOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setForm({ ...form, intakeType: option.value })}
                        className={`flex-1 h-12 rounded-xl border flex items-center justify-center gap-2 transition-all ${form.intakeType === option.value
                          ? "bg-[#189FAA]/10 border-[#189FAA] text-[#189FAA]"
                          : "bg-white border-gray-100 text-gray-600"
                          }`}
                      >
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${form.intakeType === option.value
                            ? "border-[#189FAA]"
                            : "border-gray-200"
                            }`}
                        >
                          {form.intakeType === option.value && (
                            <div className="w-2.5 h-2.5 bg-[#189FAA] rounded-full" />
                          )}
                        </div>
                        <span className="font-bold">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase mb-2 block">
                    Instruction
                  </label>
                  <TextArea
                    rows={2}
                    placeholder="Enter instruction"
                    value={form.instruction}
                    onChange={(e) =>
                      setForm({ ...form, instruction: e.target.value })
                    }
                    className="rounded-xl border-gray-100 p-3 font-medium"
                    maxLength={100}
                  />
                </div>

                <Button
                  type="dashed"
                  block
                  size="large"
                  onClick={addMedicineToList}
                  className="h-12 rounded-xl border-[#189FAA] text-[#189FAA] font-bold hover:bg-[#189FAA]/5"
                >
                  {currentEditIndex !== null ? "Update in List" : "Add to List"}
                </Button>
              </div>
            )}

            {!editingReminder && (
              <div
                className={`${isPrescriptionMode ? "w-full" : "flex-1 pl-2"
                  } flex flex-col h-full overflow-y-auto hide-scrollbar`}
              >
                {renderScheduleConfig()}
                {renderMedicineListPreview()}
              </div>
            )}

            {editingReminder && (
              <div className="flex-1 flex flex-col justify-end">
                <div className="flex gap-4">
                  <Button
                    block
                    onClick={closeScheduleModal}
                    className="h-14 rounded-2xl font-bold"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    block
                    onClick={handleSave}
                    loading={loading}
                    className="h-14 rounded-2xl bg-[#189FAA] border-none font-bold text-white"
                  >
                    Update Reminder
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        title={null}
        open={isPrescModalOpen}
        onCancel={() => setIsPrescModalOpen(false)}
        footer={null}
        width={500}
        centered
        styles={{
          mask: { backdropFilter: "blur(4px)" },
          content: { borderRadius: "28px", padding: 0, overflow: "hidden" },
        }}
      >
        <div className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-50 rounded-xl">
              <FileText className="text-[#189FAA]" size={20} />
            </div>
            <h2 className="text-xl font-bold text-[#071a3d]">
              Select Prescription
            </h2>
          </div>

          {loadingPresc ? (
            <div className="py-20 flex justify-center">
              <Spin />
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
              {prescriptions.length > 0 ? (
                prescriptions.map((presc, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelectPrescription(presc)}
                    className="p-4 bg-white border border-gray-100 rounded-2xl hover:border-[#189FAA] hover:shadow-md transition-all cursor-pointer group"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex gap-4">
                        <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center group-hover:bg-blue-50 transition-colors">
                          <Calendar
                            size={22}
                            className="text-gray-400 group-hover:text-[#189FAA]"
                          />
                        </div>

                        <div>
                          <div className="font-bold text-[#071a3d]">
                            {presc.userName}
                          </div>
                          <div className="text-xs text-gray-500 font-medium">
                            {presc.clinic_Name}
                          </div>
                          <div className="text-[10px] text-gray-400 font-bold uppercase mt-1">
                            {dayjs(presc.consultationDate).format("DD MMM YYYY")}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Tag
                          color="cyan"
                          className="m-0 rounded-lg px-2 border-none font-bold text-[10px]"
                        >
                          {presc.drugPrescription?.length || 0} Medicines
                        </Tag>
                        <ArrowRight
                          size={18}
                          className="text-gray-300 group-hover:text-[#189FAA] transition-colors"
                        />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <Empty description="No prescriptions found" />
              )}
            </div>
          )}

          <div className="mt-8">
            <Button
              block
              size="large"
              onClick={() => setIsPrescModalOpen(false)}
              className="h-14 rounded-2xl border-gray-100 text-gray-500 font-bold hover:bg-gray-50"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <style>
        {`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .medicine-modal .ant-input {
          border-radius: 12px;
          font-weight: 600;
        }
        .medicine-modal .ant-input:focus,
        .medicine-modal .ant-input-focused {
          border-color: #189FAA;
          box-shadow: 0 0 0 2px rgba(24, 159, 170, 0.1);
        }
        .medicine-modal .ant-picker {
          border-radius: 12px;
          border-color: #f3f4f6;
        }
        .medicine-modal .ant-picker:hover,
        .medicine-modal .ant-picker-focused {
          border-color: #189FAA;
        }
        `}
      </style>
    </div>
  );
};

export default MedicineReminder;